"use strict";
// Copyright (c) 2026 The Bitcoin developers
// Distributed under the MIT software license, see the accompanying
// file COPYING or http://www.opensource.org/licenses/mit-license.php.
Object.defineProperty(exports, "__esModule", { value: true });
exports.getTxNotificationMsg = void 0;
const opreturn_1 = require("./constants/opreturn");
const address_1 = require("./address");
const amounts_1 = require("./amounts");
const formatting_1 = require("./formatting");
const tokenProtocol_1 = require("./tokenProtocol");
const types_1 = require("./types");
const getTxNotificationMsg = (parsedTx, fiatPrice, userLocale, selectedFiatTicker, genesisInfo) => {
    const { parsedTokenEntries, satoshisSent, replyAddress, recipients, xecTxType, appActions, alpSwap, } = parsedTx;
    // Prices in notifications: compact (k/M/B/T) for human readability
    const xecSent = (0, amounts_1.toXec)(satoshisSent);
    const fiatSent = fiatPrice !== null ? xecSent * fiatPrice : null;
    const renderedAmount = `${(0, formatting_1.toFormattedXec)(satoshisSent, userLocale)} XEC${fiatSent !== null
        ? ` (${(0, formatting_1.toFormattedFiatNotification)(fiatSent, userLocale, selectedFiatTicker)})`
        : ''}`;
    /**
     * For a received tx, the "from" address is at the "replyAddress" key
     * We do not expect this to ever be undefined for a received tx, but include fallback in case
     *
     * For a sending tx, we render the "to" address is as recipients[0]
     * This can be undefined for self-send txs e.g. an agora cancel
     * Currently we do not render notifications that include an address for this type of tx
     * But we include the fallback to ensure never undefined
     *
     * Note we may have more than one to or from address, so these are shorthand
     * assumptions for the purpose of making useful + concise notifications
     */
    const toOrFromAddress = xecTxType === types_1.XecTxType.Received
        ? replyAddress || 'unknown'
        : recipients[0] || 'self';
    const renderedToOrFromAddress = toOrFromAddress === 'self' || toOrFromAddress === 'unknown'
        ? toOrFromAddress
        : (0, address_1.previewAddress)(toOrFromAddress);
    const formatAlpSwapQty = (atoms, tokenId) => {
        const ticker = typeof genesisInfo !== 'undefined' &&
            parsedTokenEntries[0]?.tokenId === tokenId
            ? genesisInfo.tokenTicker !== ''
                ? genesisInfo.tokenTicker
                : genesisInfo.tokenName
            : `${tokenId.slice(0, 5)}...${tokenId.slice(-5)}`;
        const qty = typeof genesisInfo !== 'undefined' &&
            parsedTokenEntries[0]?.tokenId === tokenId
            ? `${(0, formatting_1.toFormattedTokenQty)((0, amounts_1.decimalizeTokenAmount)(atoms, genesisInfo.decimals), userLocale)} `
            : `${atoms} `;
        return { qty, ticker };
    };
    if (typeof alpSwap !== 'undefined') {
        if (alpSwap.role === 'buyer') {
            // Buyer settle toasts are handled by the AlpSwap UI (from → to).
            return;
        }
        if (alpSwap.role === 'seller') {
            const sold = formatAlpSwapQty(alpSwap.toAtoms, alpSwap.toTokenId);
            const received = formatAlpSwapQty(alpSwap.fromAtoms, alpSwap.fromTokenId);
            return `Sold ${sold.qty}${sold.ticker} for ${received.qty}${received.ticker}`;
        }
        if (alpSwap.role === 'makerFee') {
            const fee = formatAlpSwapQty(alpSwap.atoms, alpSwap.tokenId);
            return `Received alp-dex fee ${fee.qty}${fee.ticker}`;
        }
        const fee = formatAlpSwapQty(alpSwap.atoms, alpSwap.tokenId);
        return `Received platform fee ${fee.qty}${fee.ticker}`;
    }
    if (parsedTokenEntries.length === 0) {
        // If this is not a token tx
        if (appActions.length === 0) {
            // If this tx has no app actions
            switch (xecTxType) {
                case types_1.XecTxType.Received: {
                    return `Received ${renderedAmount} from ${renderedToOrFromAddress}`;
                }
                case types_1.XecTxType.Sent: {
                    return `Sent ${renderedAmount} to ${renderedToOrFromAddress}`;
                }
                case types_1.XecTxType.Staking: {
                    return `New staking reward: ${renderedAmount}`;
                }
                case types_1.XecTxType.Coinbase: {
                    return `New mining reward: ${renderedAmount}`;
                }
                default: {
                    // Should never happen
                    return `${xecTxType} ${renderedAmount}`;
                }
            }
        }
        else {
            // Not a token tx
            // Has app actions
            // For notifications, we parse only the first app action
            const notificationParsedAppAction = appActions[0];
            const { lokadId, isValid, action, app } = notificationParsedAppAction;
            switch (lokadId) {
                case opreturn_1.opReturn.appPrefixesHex.airdrop: {
                    if (isValid) {
                        const { tokenId, msg } = action;
                        return `${app}: ${xecTxType} ${renderedAmount} ${xecTxType === 'Sent'
                            ? 'to holders of'
                            : `for holding`} ${tokenId.slice(0, 5)}...${tokenId.slice(-5)}${typeof msg !== 'undefined' ? ` | ${msg}` : ''}`;
                    }
                    return `${xecTxType} ${renderedAmount} | Invalid ${app}`;
                }
                case opreturn_1.opReturn.appPrefixesHex.paybutton: {
                    if (isValid) {
                        const { data } = action;
                        // We do not include nonce in notification
                        return `${app}: ${xecTxType} ${renderedAmount}${data !== '' ? ` | ${data}` : ''}`;
                    }
                    return `${xecTxType} ${renderedAmount} | Invalid ${app}`;
                }
                case opreturn_1.opReturn.appPrefixesHex.nftoa: {
                    if (isValid) {
                        const { data } = action;
                        // We do not include nonce in notification
                        return `${app} | ${xecTxType} ${renderedAmount} | ${data}`;
                    }
                    return `${xecTxType} ${renderedAmount} | Invalid ${app}`;
                }
                case opreturn_1.opReturn.appPrefixesHex.xecx: {
                    if (isValid) {
                        return `${app} | ${xecTxType} ${renderedAmount}`;
                    }
                    return `${xecTxType} ${renderedAmount} | Invalid ${app}`;
                }
                case opreturn_1.opReturn.appPrefixesHex.swap: {
                    return `${xecTxType} ${renderedAmount} | ${app}`;
                }
                case opreturn_1.opReturn.appPrefixesHex.eCashChat: {
                    if (isValid) {
                        const { msg } = action;
                        return `${app} | ${xecTxType} ${renderedAmount} | ${msg}`;
                    }
                    return `${xecTxType} ${renderedAmount} | Invalid ${app}`;
                }
                case opreturn_1.opReturn.appPrefixesHex.paywallPayment: {
                    if (isValid) {
                        const { sharedArticleTxid } = action;
                        return `${app} | ${xecTxType} ${renderedAmount} | ${sharedArticleTxid.slice(0, 5)}...${sharedArticleTxid.slice(-5)}`;
                    }
                    return `${xecTxType} ${renderedAmount} | Invalid ${app}`;
                }
                case opreturn_1.opReturn.appPrefixesHex.authPrefixHex: {
                    if (isValid) {
                        return `${app} | ${xecTxType} ${renderedAmount}`;
                    }
                    return `${xecTxType} ${renderedAmount} | Invalid ${app}`;
                }
                case opreturn_1.opReturn.appPrefixesHex.eCashChatArticle: {
                    if (isValid) {
                        return `${app} | ${xecTxType} ${renderedAmount}`;
                    }
                    return `${xecTxType} ${renderedAmount} | Invalid ${app}`;
                }
                case opreturn_1.opReturn.appPrefixesHex.cashtab: {
                    if (isValid) {
                        const { msg } = action;
                        return `${app} | ${xecTxType} ${renderedAmount} | ${msg}`;
                    }
                    return `${xecTxType} ${renderedAmount} | Invalid ${app}`;
                }
                case opreturn_1.opReturn.appPrefixesHex.pow: {
                    const verb = action && 'type' in action ? action.type : 'activity';
                    return `Proof of Writing | ${xecTxType} ${renderedAmount} | ${verb}`;
                }
                case opreturn_1.opReturn.appPrefixesHex.dice: {
                    if (isValid) {
                        const { minValue, maxValue } = action;
                        return `${app} | ${xecTxType} ${renderedAmount} | ${minValue}-${maxValue}`;
                    }
                    return `${xecTxType} ${renderedAmount} | Invalid ${app}`;
                }
                case opreturn_1.opReturn.appPrefixesHex.roll: {
                    if (isValid) {
                        const { result, roll } = action;
                        return `${app} | ${xecTxType} ${renderedAmount} | ${result} (${roll})`;
                    }
                    return `${xecTxType} ${renderedAmount} | Invalid ${app}`;
                }
                default: {
                    if (app === 'unknown') {
                        // Then it has a lokadId, just not one we recognize
                        return `${xecTxType} ${renderedAmount} | Unrecognized LOKAD ${lokadId}`;
                    }
                    else {
                        // Other unparsed OP_RETURN
                        const decoded = action && 'decoded' in action
                            ? action.decoded
                            : undefined;
                        return typeof decoded !== 'undefined'
                            ? `${xecTxType} ${renderedAmount} | Unparsed OP_RETURN: ${decoded}`
                            : `${xecTxType} ${renderedAmount} | ${app}`;
                    }
                }
            }
        }
    }
    else {
        // Parse as token tx
        // We only parse the first tokenEntry for tx notifications
        const parsedTokenEntry = parsedTokenEntries[0];
        const { tokenId, renderedTokenType, renderedTxType, tokenSatoshis, nftFanInputsCreated, } = parsedTokenEntry;
        const renderedTokenQty = typeof genesisInfo !== 'undefined'
            ? `${(0, formatting_1.toFormattedTokenQty)((0, amounts_1.decimalizeTokenAmount)(String(tokenSatoshis), genesisInfo.decimals), userLocale)} `
            : '';
        const renderedTicker = typeof genesisInfo !== 'undefined'
            ? `${genesisInfo.tokenTicker !== ''
                ? genesisInfo.tokenTicker
                : genesisInfo.tokenName}`
            : `${tokenId.slice(0, 5)}...${tokenId.slice(-5)}`;
        switch (renderedTxType) {
            case types_1.ParsedTokenTxType.AgoraOffer: {
                // Agora listing tx notification are handled by UI components that support listing
                return;
            }
            case types_1.ParsedTokenTxType.AgoraCancel: {
                // Agora cancel tx notifications are handled by UI components that support cancels
                return;
            }
            case types_1.ParsedTokenTxType.AgoraRelist: {
                // Agora relist tx notifications are handled by UI components that support relists
                return;
            }
            case types_1.ParsedTokenTxType.AgoraBuy: {
                // Agora purchase tx notifications are handled by UI components that support buys
                return;
            }
            case types_1.ParsedTokenTxType.AgoraSale: {
                // Sales are incoming txs, the user is not creating this from a UI component
                // Parse here
                return `Sold ${renderedTokenQty}${renderedTicker} for ${renderedAmount}`;
            }
            case types_1.ParsedTokenTxType.BlitzPlay: {
                return `Blitz play ${renderedTokenQty}${renderedTicker}`;
            }
            case types_1.ParsedTokenTxType.FanOut: {
                return `Created ${nftFanInputsCreated} NFT mint inputs for ${renderedTicker}`;
            }
            case 'SEND': {
                return `${xecTxType === types_1.XecTxType.Received ? 'Received' : 'Sent'} ${renderedTokenQty}${renderedTicker}`;
            }
            case 'MINT': {
                return `🔨 Minted ${renderedTokenQty}${renderedTicker}`;
            }
            case 'BURN': {
                return `🔥 Burned ${renderedTokenQty}${renderedTicker}`;
            }
            case 'GENESIS': {
                if (renderedTokenType === tokenProtocol_1.RenderedTokenType.NFT) {
                    return `NFT | 👨‍🎨 Minted ${renderedTokenQty}${renderedTicker}`;
                }
                return `⚗️ Genesis | Created ${renderedTokenQty}${renderedTicker}`;
            }
            default: {
                // Should never happen
                return `${xecTxType} ${renderedAmount}`;
            }
        }
    }
};
exports.getTxNotificationMsg = getTxNotificationMsg;
//# sourceMappingURL=getTxNotificationMsg.js.map